import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

interface RecaptchaClientApi {
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
  ready?: (cb: () => void) => void;
}

@Injectable({ providedIn: 'root' })
export class RecaptchaClientService {
  private readonly recaptchaScriptId = 'fliko-recaptcha-script';
  private readonly recaptchaScriptUrls = [
    'https://www.google.com/recaptcha/api.js',
    'https://www.recaptcha.net/recaptcha/api.js'
  ];
  private readonly recaptchaScriptLoadTimeoutMs = 10000;
  private readonly recaptchaApiPollIntervalMs = 100;
  private recaptchaApiLoadPromise: Promise<void> | null = null;

  constructor(@Inject(DOCUMENT) private document: Document) {}

  /**
   * Executes reCAPTCHA action and returns token used for backend verification
   * @param {string} siteKey
   * @param {string} action
   * @return {Promise<string>}
   */
  public async executeAction(siteKey: string, action: string): Promise<string> {
    if (!siteKey) return '';

    await this.ensureRecaptchaApiLoaded(siteKey);
    await this.waitForRecaptchaReady();

    const recaptchaApi = this.getRecaptchaApi();
    if (!recaptchaApi?.execute) throw new Error('reCAPTCHA execute unavailable');

    const token = await recaptchaApi.execute(siteKey, { action });
    return typeof token === 'string' ? token : '';
  }

  /**
   * Ensures reCAPTCHA API script is loaded and reuses in-flight load promise
   * @param {string} siteKey
   * @return {Promise<void>}
   */
  private ensureRecaptchaApiLoaded(siteKey: string): Promise<void> {
    if (this.isRecaptchaApiLoaded()) return this.waitForRecaptchaReady();

    if (this.recaptchaApiLoadPromise) return this.recaptchaApiLoadPromise;

    this.recaptchaApiLoadPromise = this.loadRecaptchaScript(siteKey).finally(() => {
      this.recaptchaApiLoadPromise = null;
    });

    return this.recaptchaApiLoadPromise;
  }

  /**
   * Checks if global reCAPTCHA API with execute function is available
   * @return {boolean}
   */
  private isRecaptchaApiLoaded(): boolean {
    return !!this.getRecaptchaApi()?.execute;
  }

  /**
   * Waits for grecaptcha.ready callback when API exposes it
   * @return {Promise<void>}
   */
  private waitForRecaptchaReady(): Promise<void> {
    const recaptchaApi = this.getRecaptchaApi();
    if (!recaptchaApi?.ready) return Promise.resolve();

    return new Promise<void>((resolve) => {
      recaptchaApi.ready?.(() => resolve());
    });
  }

  /**
   * Loads reCAPTCHA script from fallback domains and waits until API is ready
   * @param {string} siteKey
   * @return {Promise<void>}
   */
  private async loadRecaptchaScript(siteKey: string): Promise<void> {
    if (this.isRecaptchaApiLoaded()) {
      await this.waitForRecaptchaReady();
      return;
    }

    const existingScript = this.document.getElementById(this.recaptchaScriptId) as HTMLScriptElement | null;
    if (existingScript) existingScript.remove();

    for (const scriptUrl of this.recaptchaScriptUrls) {
      try {
        await this.injectRecaptchaScript(scriptUrl, siteKey);
        await this.waitForRecaptchaApiAvailable();
        await this.waitForRecaptchaReady();
        return;
      } catch {
        const scriptToRemove = this.document.getElementById(this.recaptchaScriptId) as HTMLScriptElement | null;
        if (scriptToRemove) scriptToRemove.remove();
      }
    }

    throw new Error('reCAPTCHA script load failed');
  }

  /**
   * Polls until global reCAPTCHA API becomes available or timeout is reached
   * @return {Promise<void>}
   */
  private waitForRecaptchaApiAvailable(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const currentWindow = this.getWindow();
      if (!currentWindow) {
        reject(new Error('window is not available'));
        return;
      }

      const endTime = Date.now() + this.recaptchaScriptLoadTimeoutMs;
      const tick = () => {
        if (this.isRecaptchaApiLoaded()) {
          resolve();
          return;
        }
        if (Date.now() >= endTime) {
          reject(new Error('reCAPTCHA API is not available'));
          return;
        }
        currentWindow.setTimeout(tick, this.recaptchaApiPollIntervalMs);
      };
      tick();
    });
  }

  /**
   * Returns normalized wrapper for global grecaptcha API when available
   * @return {RecaptchaClientApi | null}
   */
  private getRecaptchaApi(): RecaptchaClientApi | null {
    const grecaptcha = (this.getWindow() as any)?.grecaptcha;
    if (!grecaptcha) return null;

    if (typeof grecaptcha.execute === 'function') {
      return {
        execute: grecaptcha.execute.bind(grecaptcha),
        ready: typeof grecaptcha.ready === 'function' ? grecaptcha.ready.bind(grecaptcha) : undefined
      };
    }

    return null;
  }

  /**
   * Injects script tag for provided reCAPTCHA endpoint
   * @param {string} scriptUrl
   * @param {string} siteKey
   * @return {Promise<void>}
   */
  private injectRecaptchaScript(scriptUrl: string, siteKey: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const currentWindow = this.getWindow();
      if (!currentWindow || !this.document.head) {
        reject(new Error('DOM is not available'));
        return;
      }

      const script = this.document.createElement('script');
      let timeoutId: number | null = null;
      let isSettled = false;

      const finalize = (onDone: () => void) => {
        if (isSettled) return;
        isSettled = true;
        if (timeoutId !== null) currentWindow.clearTimeout(timeoutId);
        onDone();
      };

      script.id = this.recaptchaScriptId;
      script.src = `${scriptUrl}?render=${encodeURIComponent(siteKey)}`;
      script.async = true;
      script.defer = true;
      script.onload = () => finalize(() => resolve());
      script.onerror = () => finalize(() => reject(new Error('reCAPTCHA script load failed')));
      this.document.head.appendChild(script);

      timeoutId = currentWindow.setTimeout(() => {
        finalize(() => reject(new Error('reCAPTCHA script load timeout')));
      }, this.recaptchaScriptLoadTimeoutMs);
    });
  }

  /**
   * Returns browser window object for environments where DOM is available
   * @return {Window | null}
   */
  private getWindow(): Window | null {
    return this.document.defaultView ?? null;
  }
}
